import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getCurrencyByCode, getPlatformData } from '../../../store/common/selectors';

type TSummary = { [key: string]: {
  totalOrders: number,
  totalProducts: number,
  totalSubTotal: number,
  totalCommission: number,
} };

export function useDownloadReportCSV() {
  const { locations } = useAppSelector(getPlatformData);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const { t } = useTranslation(['inputs', 'timesheets', 'dashboard', 'commissions']);

  const locationIdToNameMap = locations.reduce((acc, location) => {
    acc[location.id] = location.name;
    return acc;
  }, {} as { [key: string]: string });

  const downloadReportCSV = (summary: TSummary, filename = 'mgm_summary.csv') => {
    const rows = [
      [
        t('location'),
        t('orders', { ns: 'timesheets' }),
        t('products', { ns: 'dashboard' }),
        t('netSales', { ns: 'commissions' }),
        t('commissions', { ns: 'commissions' }),
      ],
    ];

    Object.keys(summary).forEach((locationId) => {
      const {
        totalOrders, totalProducts, totalSubTotal, totalCommission,
      } = summary[locationId];
      rows.push([
        locationIdToNameMap[locationId],
        String(totalOrders),
        String(totalProducts),
        `${getCurrency().mark}${String((totalSubTotal || 0).toFixed(2))}`,
        `${getCurrency().mark}${String((totalCommission || 0).toFixed(2))}`,
      ]);
    });

    const csvContent = rows.map((e) => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create a hidden anchor element and trigger download in the background
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none'; // Make the element invisible
    document.body.appendChild(link);
    link.click(); // Trigger the download
    document.body.removeChild(link); // Remove the element after triggering the download

    // Revoke the URL to release memory
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  };

  const downloadDailyReportCSV = (summary: any, filename = 'mgm_daily.csv') => {
    const rows = [
      [
        t('date'),
        t('location'),
        t('orders', { ns: 'timesheets' }),
        t('products', { ns: 'dashboard' }),
        t('netSales', { ns: 'commissions' }),
        t('commissions', { ns: 'commissions' }),
      ],
    ];

    Object.keys(summary).forEach((day) => {
      let isFirstRowForDay = true; // To track if it's the first row for this day

      Object.keys(summary[day]).forEach((locationId) => {
        const locationName = locationIdToNameMap[locationId]; // Get the location name
        const {
          totalOrders, totalProducts, totalSubTotal, totalCommission,
        } = summary[day][locationId];

        // If it's the first row for the day, display the day, otherwise leave it blank
        rows.push([
          isFirstRowForDay ? day : '', // Only show the day for the first entry
          locationName, // Location name
          String(totalOrders), // Total orders for the day
          String(totalProducts), // Total products for the day
          `${getCurrency().mark}${String((totalSubTotal || 0).toFixed(2))}`, // Total sales for the day
          `${getCurrency().mark}${String((totalCommission || 0).toFixed(2))}`, // Total commission for the day
        ]);

        isFirstRowForDay = false; // Mark that the day has already been displayed
      });
    });

    const csvContent = rows.map((e) => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create a hidden anchor element and trigger download in the background
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none'; // Make the element invisible
    document.body.appendChild(link);
    link.click(); // Trigger the download
    document.body.removeChild(link); // Remove the element after triggering the download

    // Revoke the URL to release memory
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  };

  return { downloadReportCSV, downloadDailyReportCSV };
}
